/**
 * Typeform API Integration Service
 * Handles fetching survey responses and form data from Typeform
 * Uses REST API directly for browser compatibility
 */

class TypeformService {
  constructor() {
    this.baseUrl = 'https://api.typeform.com';
    this.responseCache = new Map();
    this.definitionCache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5 minutes cache for responses
    this.definitionCacheTimeout = 24 * 60 * 60 * 1000; // 24 hours cache for definitions
    // Token is now handled server-side via proxy endpoints for security
    
    // Mapping of custom URL paths to actual Typeform IDs
    this.customUrlMapping = {
      'tcl-v1-en': 'esHPzD6e',  // The Connected Leader EN
      'ctl-learners': 'SIS9velg', // Archive TCL
      // Add more mappings as needed
    };
  }

  /**
   * Initialize the Typeform service
   * Note: Token is now handled server-side via proxy endpoints for security
   */
  init() {
    // Token is no longer needed client-side - handled by proxy endpoints
    return true;
  }

  /**
   * Check if cache is still valid
   */
  isCacheValid(formId) {
    const cached = this.responseCache.get(formId);
    if (!cached) return false;
    
    const now = Date.now();
    return now - cached.timestamp < this.cacheTimeout;
  }

  /**
   * Get responses for a specific form
   * @param {string} formId - The Typeform form ID
   * @returns {Promise<Object>} Response data including count and items
   */
  async getFormResponses(formId) {
    if (!formId) {
      console.warn('No form ID provided');
      return { total_items: 0, items: [] };
    }

    // Check cache first
    if (this.isCacheValid(formId)) {
      const cached = this.responseCache.get(formId);
      console.log(`Returning cached responses for form ${formId}:`, cached.data.total_items, 'items');
      return cached.data;
    }

    console.log(`Fetching fresh responses for form ${formId}`);
    try {
      // Use proxy endpoint to avoid CORS issues
      let apiUrl;
      let headers = {
        'Accept': 'application/json',
      };

      // Check if we're in pure Vite dev mode (no Wrangler Pages Functions available)
      const isViteDevMode = import.meta.env.DEV && !window.location.pathname.startsWith('/__');

      if (isViteDevMode && import.meta.env.VITE_TYPEFORM_ACCESS_TOKEN) {
        // In Vite dev mode: use direct API with token (CORS might be blocked, but allows HMR)
        apiUrl = `${this.baseUrl}/forms/${formId}/responses?page_size=1000`;
        headers['Authorization'] = `Bearer ${import.meta.env.VITE_TYPEFORM_ACCESS_TOKEN}`;
        console.log('🔧 DEV MODE: Using direct Typeform API (CORS extension may be required)');
      } else {
        // Production or Wrangler mode: use secure server-side proxy endpoint
        // Token is handled server-side for security (never exposed to client)
        apiUrl = `/api/typeform/${formId}/responses?page_size=1000`;
      }

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Log what we received
      console.log(`Received ${data.total_items || 0} responses for form ${formId}`);

      // Only cache if we got responses (avoid caching empty results)
      // This helps with the initial load issue where API returns 0 items
      if (data.total_items > 0 || this.responseCache.has(formId)) {
        this.responseCache.set(formId, {
          data: data,
          timestamp: Date.now(),
        });
      }

      return data;
    } catch (error) {
      console.error(`Failed to fetch responses for form ${formId}:`, error);
      
      // Return empty response on error
      return { total_items: 0, items: [] };
    }
  }

  /**
   * Get just the response count for a form (lightweight)
   * @param {string} formId - The Typeform form ID
   * @returns {Promise<number>} Number of responses
   */
  async getResponseCount(formId) {
    if (!formId) return 0;

    // For count, we can use the cached data if available
    if (this.isCacheValid(formId)) {
      const cached = this.responseCache.get(formId);
      return cached.data.total_items || 0;
    }

    try {
      let apiUrl;
      let headers = {
        'Accept': 'application/json',
      };
      
      // Always use the secure server-side proxy endpoint
      // Token is handled server-side for security (never exposed to client)
      apiUrl = `/api/typeform/${formId}/responses?page_size=1`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      // Cache even this minimal response
      this.responseCache.set(formId, {
        data: { total_items: data.total_items, items: [] },
        timestamp: Date.now(),
      });

      return data.total_items || 0;
    } catch (error) {
      console.error(`Failed to fetch response count for form ${formId}:`, error);
      return 0;
    }
  }

  /**
   * Get form definition including all questions and their types
   * @param {string} formId - The Typeform form ID
   * @returns {Promise<Object>} Form definition with fields array
   */
  async getFormDefinition(formId) {
    if (!formId) {
      console.warn('No form ID provided for definition');
      return null;
    }

    // Check definition cache first (24 hour TTL)
    const cached = this.definitionCache.get(formId);
    if (cached) {
      const now = Date.now();
      if (now - cached.timestamp < this.definitionCacheTimeout) {
        return cached.data;
      }
    }

    try {
      let apiUrl;
      let headers = {
        'Accept': 'application/json',
      };
      
      // Always use the secure server-side proxy endpoint
      // Token is handled server-side for security (never exposed to client)
      apiUrl = `/api/typeform/${formId}/form`;
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: headers,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch form definition: ${response.status}`);
      }

      const data = await response.json();

      // Cache the definition
      this.definitionCache.set(formId, {
        data: data,
        timestamp: Date.now(),
      });

      return data;
    } catch (error) {
      console.error(`Failed to fetch form definition for ${formId}:`, error);
      return null;
    }
  }

  /**
   * Get responses filtered by SO number (cohort)
   * @param {string} formId - The Typeform form ID
   * @param {string} soNumber - The SO number to filter by
   * @returns {Promise<Object>} Filtered response data
   */
  async getFormResponsesByCohort(formId, soNumber) {
    if (!formId || !soNumber) {
      console.warn('Form ID and SO number are required');
      return { total_items: 0, items: [] };
    }

    try {
      // First get all responses (using existing method with caching)
      const allResponses = await this.getFormResponses(formId);
      
      if (!allResponses || !allResponses.items) {
        console.warn('No responses received from Typeform API');
        return { total_items: 0, items: [] };
      }

      // Debug: Log all hidden fields to see what we're working with
      if (import.meta.env.DEV && allResponses.items.length > 0) {
        console.log('Sample response hidden fields from first response:', allResponses.items[0].hidden);
        console.log('Looking for SO number:', soNumber);
        
        // Check what values cohort field has
        const cohortValues = allResponses.items.map(r => r.hidden?.cohort).filter(Boolean);
        const uniqueCohorts = [...new Set(cohortValues)];
        console.log('Unique cohort values found:', uniqueCohorts);
      }

      // Filter responses where hidden.cohort matches the SO number
      // The cohort field should match exactly (as a string)
      let filteredItems = allResponses.items.filter(response => {
        // Check if hidden fields exist and cohort matches
        return response.hidden && 
               response.hidden.cohort && 
               response.hidden.cohort.toString() === soNumber.toString();
      });

      // If no exact matches found but there are responses, it might be a data issue
      // In development or when there's only one cohort, show all responses with a warning
      let cohortMismatch = false;
      if (filteredItems.length === 0 && allResponses.items.length > 0) {
        const cohortValues = allResponses.items.map(r => r.hidden?.cohort).filter(Boolean);
        const uniqueCohorts = [...new Set(cohortValues)];
        
        // If there's only one cohort value in all responses, use those responses
        // This handles cases where the survey was set up with wrong SO number
        if (uniqueCohorts.length === 1) {
          console.warn(`Cohort mismatch: Expected SO ${soNumber}, but all responses have cohort ${uniqueCohorts[0]}. Showing all responses.`);
          filteredItems = allResponses.items;
          cohortMismatch = true;
        }
      }

      // Log for debugging in development
      if (import.meta.env.DEV) {
        console.log(`Filtered ${filteredItems.length} responses for SO ${soNumber} from ${allResponses.items.length} total`);
        if (filteredItems.length > 0) {
          console.log('Sample filtered response hidden fields:', filteredItems[0].hidden);
        } else if (allResponses.items.length > 0) {
          console.log('No responses matched SO number. Check cohort values above.');
        }
      }

      return {
        total_items: filteredItems.length,
        items: filteredItems,
        page_count: 1,
        // Include metadata about filtering
        filtered_by: soNumber,
        total_before_filter: allResponses.total_items,
        cohort_mismatch: cohortMismatch,
        actual_cohort: cohortMismatch && filteredItems.length > 0 ? filteredItems[0].hidden?.cohort : null
      };
    } catch (error) {
      console.error(`Failed to fetch responses for SO ${soNumber}:`, error);
      return { total_items: 0, items: [] };
    }
  }

  /**
   * Clear cache for a specific form or all forms
   * @param {string} formId - Optional form ID to clear specific cache
   */
  clearCache(formId = null) {
    if (formId) {
      this.responseCache.delete(formId);
      this.definitionCache.delete(formId);
    } else {
      this.responseCache.clear();
      this.definitionCache.clear();
    }
  }

  /**
   * Extract form ID from a Typeform URL
   * @param {string} url - Typeform URL
   * @returns {string|null} Form ID or null if not found
   */
  extractFormIdFromUrl(url) {
    if (!url) return null;

    try {
      // Handle various Typeform URL formats
      
      // Standard format: https://form.typeform.com/to/ABC123
      let match = url.match(/typeform\.com\/to\/([a-zA-Z0-9]+)/);
      if (match) {
        return match[1];
      }
      
      // Custom domain format: https://getavenue.typeform.com/form-name
      // Extract the custom path and look it up in our mapping
      match = url.match(/typeform\.com\/([a-zA-Z0-9-]+)(?:[#?]|$)/);
      if (match && !match[1].includes('/')) {
        const customPath = match[1];
        const formId = this.customUrlMapping[customPath];
        
        if (formId) {
          console.log(`Mapped custom URL path '${customPath}' to form ID: ${formId}`);
          return formId;
        } else {
          console.warn(`No mapping found for custom URL path: ${customPath}`);
          return null;
        }
      }
      
      console.warn('Could not extract form ID from URL:', url);
      return null;
    } catch (error) {
      console.error('Failed to extract form ID from URL:', error);
      return null;
    }
  }
}

// Create and export a singleton instance
const typeformService = new TypeformService();
export default typeformService;